import React, { ComponentType, ReactNode } from "react";

/**
 * Intermediate tree representation.
 * Built imperatively, then rendered to React elements.
 */

// Internal type for tree nodes - supports both HTML elements and components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TreeElementType = string | ComponentType<any>;

export type Node = TextNode | ElementNode | FragmentNode;

interface TextNode {
    type: "text";
    value: string;
}

export interface ElementNode {
    type: "element";
    tag: TreeElementType;
    props: Record<string, unknown>;
    children: Node[];
}

interface FragmentNode {
    type: "fragment";
    children: Node[];
}

/**
 * Create a text node
 */
export function text(value: string): TextNode {
    return { type: "text", value };
}

/**
 * Create an element node (like React.createElement but for our tree)
 */
export function createElement(
    tag: TreeElementType,
    props: Record<string, unknown> = {},
    ...children: (Node | string)[]
): ElementNode {
    return {
        type: "element",
        tag,
        props,
        children: children.map((child) =>
            typeof child === "string" ? text(child) : child,
        ),
    };
}

/**
 * Create a fragment node (for grouping without a wrapper element)
 */
export function fragment(...children: (Node | string)[]): FragmentNode {
    return {
        type: "fragment",
        children: children.map((child) =>
            typeof child === "string" ? text(child) : child,
        ),
    };
}

/**
 * Append a child to a parent node (mutates parent)
 */
export function appendChild(
    parent: ElementNode | FragmentNode,
    child: Node | string,
): void {
    parent.children.push(typeof child === "string" ? text(child) : child);
}

/**
 * Props that should only be passed to component functions, not HTML elements
 */
const INTERNAL_PROPS = [
    "block",
    "blocks",
    "inlineStyleRange",
    "entity",
    "entityRange",
    "data",
];

/**
 * Filter out internal props when rendering to an HTML element
 */
function filterPropsForHtml(
    props: Record<string, unknown>,
): Record<string, unknown> {
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        if (!INTERNAL_PROPS.includes(key)) {
            filtered[key] = value;
        }
    }
    return filtered;
}

/**
 * Render the intermediate tree to React elements.
 * This is the second pass - tree is already built.
 */
let keyCounter = 0;

export function renderToReact(node: Node): ReactNode {
    switch (node.type) {
        case "text":
            return node.value;

        case "fragment":
            return React.createElement(
                React.Fragment,
                null,
                ...node.children.map((child) => renderToReact(child)),
            );

        case "element": {
            const { tag, props, children } = node;
            const key = `rt3-${keyCounter++}`;

            if (typeof tag === "string") {
                // HTML element - filter out internal props
                return React.createElement(
                    tag,
                    { ...filterPropsForHtml(props), key },
                    ...children.map((child) => renderToReact(child)),
                );
            } else {
                // It's a component - pass all props
                return React.createElement(
                    tag,
                    { ...props, key },
                    ...children.map((child) => renderToReact(child)),
                );
            }
        }
    }
}

/**
 * Reset key counter (useful for tests to get deterministic output)
 */
export function resetKeyCounter(): void {
    keyCounter = 0;
}
